/**
 * When to ask for an older page of a channel's history, and when to stop.
 *
 * Both clients used to fetch the last 50 messages and treat that as the whole
 * past. The rules are small but each one is a trap if it's wrong: too eager and
 * the reader triggers a fetch on every wheel tick; too shy and the history
 * stalls; a missing stop condition and the client asks the server for the same
 * empty page forever.
 */

/** Messages per page. One page opens a channel; one more per scroll to the top. */
export const HISTORY_PAGE = 50;

/** How close to the top (px) counts as "about to run out of history". Generous
 *  on purpose: fetching a bit early means the rows are usually already there
 *  when the reader arrives, instead of a visible stall. */
export const HISTORY_PREFETCH_PX = 400;

/**
 * A page shorter than the limit is the only honest proof that we've reached the
 * first message: the API has no "hasMore" flag, and a full page always leaves
 * the possibility of more behind it.
 */
export function atStartAfterPage(pageLength: number, limit = HISTORY_PAGE): boolean {
  return pageLength < limit;
}

/** Whether a scroll position should trigger the next page. */
export function shouldLoadOlder(opts: {
  scrollTop: number;
  loading: boolean;
  atStart: boolean;
  /** No handler = the caller doesn't support paging (e.g. a pinned-only view). */
  canLoad: boolean;
  prefetchPx?: number;
}): boolean {
  if (!opts.canLoad || opts.loading || opts.atStart) return false;
  return opts.scrollTop < (opts.prefetchPx ?? HISTORY_PREFETCH_PX);
}

/**
 * Where to put the scroll position after older rows land above the viewport.
 *
 * Distance-from-bottom is the invariant: prepending grows the content above, so
 * keeping `scrollTop` would shove the reader backwards through the history they
 * were already looking at.
 */
export function restoredScrollTop(scrollHeight: number, anchorFromBottom: number): number {
  return Math.max(0, scrollHeight - anchorFromBottom);
}

/** How many older pages a jump (a reply quote, a pin, a search hit) may pull in
 *  looking for its target before giving up: 20 × 50 = the last 1000 messages. */
export const JUMP_MAX_PAGES = 20;

/**
 * The next step of a jump to a message that may not be loaded yet.
 *
 * `scroll` — it's in the list; `load` — ask for one more older page; `wait` — a
 * page is already on its way; `missing` — it can't be reached (deleted, or older
 * than the page budget), so say so instead of silently doing nothing.
 */
export function jumpStep(opts: {
  found: boolean;
  loading: boolean;
  atStart: boolean;
  canLoad: boolean;
  pagesLoaded: number;
  maxPages?: number;
}): 'scroll' | 'load' | 'wait' | 'missing' {
  if (opts.found) return 'scroll';
  if (opts.loading) return 'wait';
  if (!opts.canLoad || opts.atStart || opts.pagesLoaded >= (opts.maxPages ?? JUMP_MAX_PAGES)) return 'missing';
  return 'load';
}
